import { useCallback, useEffect, useState } from 'react';

import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from '../api.js';
import { formatDate } from '../labels.js';
import { navigate } from '../router.js';

/**
 * What has happened that you did not do yourself.
 *
 * Unread only by default. A notification list is a prompt to act, not an
 * archive — the moment it becomes a history nobody reads it, and then it stops
 * doing the one job it has.
 *
 * Nothing here decides what is notify-worthy; the server does. This screen only
 * shows what arrived, and gets out of the way once it is dealt with.
 */

interface Props {
  /** Lets the app bar's count follow along. */
  readonly onUnreadChanged: (unread: number) => void;
}

export function Notifications({ onUnreadChanged }: Props): JSX.Element {
  const [items, setItems] = useState<readonly Notification[] | null>(null);
  const [showRead, setShowRead] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (includeRead: boolean) => {
      try {
        const page = await listNotifications(includeRead);
        setItems(page.data);
        onUnreadChanged(page.unread);
      } catch {
        setItems([]);
      }
    },
    [onUnreadChanged],
  );

  useEffect(() => {
    void load(showRead);
  }, [load, showRead]);

  async function open(item: Notification): Promise<void> {
    setBusy(true);
    try {
      if (!item.read_at) {
        const page = await markNotificationRead(item.id);
        setItems(page.data);
        onUnreadChanged(page.unread);
      }
      if (item.deep_link) navigate(item.deep_link);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>Notifications</h2>
        <div className="head-actions">
          <button
            type="button"
            className={showRead ? 'chip-button current' : 'chip-button'}
            aria-pressed={showRead}
            onClick={() => setShowRead((v) => !v)}
          >
            Include read
          </button>
          {items && items.some((i) => !i.read_at) && (
            <button
              type="button"
              className="inline secondary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const page = await markAllNotificationsRead();
                  onUnreadChanged(page.unread);
                  await load(showRead);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      {items === null && <p aria-live="polite">Loading…</p>}

      {items?.length === 0 && (
        <div className="empty">
          <p>
            <strong>{showRead ? 'Nothing here yet.' : 'Nothing new.'}</strong>
          </p>
          <p>
            You are told when somebody assigns you work, comments on your work, or blocks it.
            Never about your own actions.
          </p>
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="task-list">
          {items.map((item) => (
            <li
              key={item.id}
              className={`task-row notif sev-${item.severity}${item.read_at ? ' is-read' : ''}`}
            >
              <div className="task-main">
                <span className="task-title">{item.title}</span>
                {item.body && <span className="task-description">{item.body}</span>}
                <span className="task-meta">
                  {/*
                    Severity IN WORDS. It used to be `sev-urgent` on the row and
                    nothing else, so a critical alert looked exactly like a
                    routine one to anybody who could not tell the colours apart,
                    and to everybody on a printout. `10`: status is never
                    carried by colour alone.
                  */}
                  {item.severity !== 'info' && (
                    <span className="task-overdue">
                      {item.severity === 'urgent' ? 'Urgent' : 'Needs a look'}
                    </span>
                  )}
                  <span>{formatDate(item.created_at)}</span>
                  {!item.read_at && <span className="unread-dot">New</span>}
                </span>
              </div>

              <span className="row-actions">
                {item.deep_link && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => void open(item)}
                  >
                    Open<span className="visually-hidden"> — {item.title}</span>
                  </button>
                )}
                {!item.read_at && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const page = await markNotificationRead(item.id);
                        onUnreadChanged(page.unread);
                        await load(showRead);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Mark read<span className="visually-hidden"> — {item.title}</span>
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
